/**
 * What a file card (OutputFileBlock) says when its file cannot be downloaded
 * (agion/Agion#2003): each text says what happened and what the user can do.
 * A failed download is told apart by the HTTP status of the host-file proxy.
 */

interface OutputFileCopy {
  /** i18n key of the text */
  key: string
  /** English fallback when the key is missing */
  fallback: string
}

export const OUTPUT_FILE_TEXTS = {
  noHost: {
    key: 'chat:outputFile.noHost',
    fallback: "Files from this assistant can't be downloaded here yet.",
  },
  noLongerAvailable: {
    key: 'chat:outputFile.noLongerAvailable',
    fallback: 'This file is no longer available. Ask the assistant to create it again.',
  },
  noAccess: {
    key: 'chat:outputFile.noAccess',
    fallback: "You don't have access to this file.",
  },
  hostOffline: {
    key: 'chat:outputFile.hostOffline',
    fallback: 'The host that holds this file is offline. Try again when it is connected.',
  },
  downloadFailed: {
    key: 'chat:outputFile.downloadFailed',
    fallback: 'Download failed. Try again.',
  },
} as const satisfies Record<string, OutputFileCopy>

export type OutputFileText = keyof typeof OUTPUT_FILE_TEXTS

type Translate = (key: string, fallback: string) => string

/** The translated text. */
export function outputFileText(t: Translate, text: OutputFileText): string {
  const { key, fallback } = OUTPUT_FILE_TEXTS[text]
  return t(key, fallback)
}

/** The text for a download that failed with this HTTP status; undefined when the request got no response. */
export function downloadFailureText(status: number | undefined): OutputFileText {
  switch (status) {
    case 404:
      return 'noLongerAvailable'
    case 403:
      return 'noAccess'
    case 502:
    case 503:
    case 504:
      return 'hostOffline'
    default:
      return 'downloadFailed'
  }
}

/**
 * The HTTP status a failed download carries (HostFileDownloadError.status in
 * services/host-api.ts). Read from the value, not by class, so a caller does
 * not depend on the api module's classes.
 */
export function httpStatusOf(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined
  const status = (err as { status?: unknown }).status
  return typeof status === 'number' && Number.isInteger(status) ? status : undefined
}
